/* eslint-disable no-restricted-syntax */
import indexPage from '@/fish/pages/index'
import mapComponent from '@/fish/pages/fish-map'
import routeNames from '@/fish/router/constants'
import accountRoutes from '@/fish/router/account-settings'
import myContentRoutes from '@/fish/router/my-content'
import offlineRoutes from '@/fish/router/offline'
import routeBuilderRoutes from '@/fish/router/route-builder'
import queryRoutes from '@/fish/router/query'
import waterbodyRoutes from '@/fish/router/waterbodies'
import weatherRoutes from '@/fish/router/weather'
import waterAccessRoutes from '@/fish/router/water-access'
import fishingAreaRoutes from '@/fish/router/fishing-areas'
import regionsRoutes from '@/fish/router/regions'
import searchRoutes from '@/fish/router/search'
import { createNewUserNavigationGuard } from '@/onboarding/service'
import { hasValidUserSession, signinRedirect } from '@/services/identity-client'
import { IdentityInitiationIntent } from '@/services/identity-types'
import { fishOnboardingRoutes } from '@/fish/router/onboarding-routes'
import { fishRedeemRoutes } from '@/router/shared/redeem'
import {
  showLandingPagePreview,
  bypassLandingForExistingUser,
} from '@/router/shared/login-route'
import { getShareIdFromRoute } from '@/lib/share'
import split from '@/lib/split'

const newUserNavGuard = (to, from) =>
  createNewUserNavigationGuard({ app: 'fish', startTrial: false })(to, from)

/** @type {import('vue-router').RouteRecordRaw[]} */
export default [
  {
    path: '/fish',
    component: indexPage,
    redirect: '/fish/map',
    children: [
      // SHARING
      {
        path: '/incoming-share',
        name: 'incomingShare',
        component: () => import('@/pages/incoming-share'),
        meta: {
          preAuthRoute: true,
        },
        // FUTURE: Remove beforeEnter and split.initialize() when gd_disable_onx_trial split is removed
        beforeEnter() {
          split.initialize()
        },
      },
      {
        // NOTE: do not change this route. Referenced as a mobile Universal Link.
        // `id` is sent as a query parameter for compatibility reasons.
        name: 'share-receive-fish',
        path: 'share/content',
        component: () => import('@/pages/share-receive-v2.vue'),
        meta: {
          preAuthRoute: true,
        },
        async beforeEnter(to) {
          if (await hasValidUserSession()) {
            // already logged in - skip the landing page
            const queryLocation = to.query?.query_location
            if (queryLocation) {
              return decodeURIComponent(queryLocation)
            }
            return {
              name: 'content-share-accept-fish',
              params: { shareID: getShareIdFromRoute(to) },
            }
          }
          return bypassLandingForExistingUser(to, to.fullPath)
        },
      },
      {
        path: 'login',
        component: () => import('@/fish/pages/login'),
        name: routeNames.LOGIN_FISH,
        meta: {
          preAuthRoute: true,
        },
        async beforeEnter(to) {
          if (showLandingPagePreview(to)) {
            return bypassLandingForExistingUser(to, '/fish')
          }

          if (await hasValidUserSession()) {
            return '/fish'
          }

          const after_login = to.redirectedFrom?.fullPath ?? '/fish'
          // The global header's Login smart link sends intent=login. Other
          // entries, like logout redirects, keep the no-intent default event.
          const userIntent =
            to.query?.intent === 'login'
              ? IdentityInitiationIntent.Login
              : undefined
          // Bypass login page and do redirect signin flow
          return await signinRedirect(
            {
              state: {
                after_login,
              },
            },
            { userIntent }
          )
        },
      },
      {
        path: 'create-account',
        component: () => import('@/fish/pages/login'),
        name: routeNames.CREATE_ACCOUNT_FISH,
        meta: {
          preAuthRoute: true,
        },
        async beforeEnter(to) {
          // Bypass create account page and do redirect ID create account flow
          if (await hasValidUserSession()) {
            return '/fish'
          }
          const after_login =
            to.query?.after_login ?? to.redirectedFrom?.fullPath ?? '/fish'
          return await signinRedirect(
            {
              state: {
                after_login,
              },
              prompt: 'registration',
            },
            { userIntent: IdentityInitiationIntent.CreateAccount }
          )
        },
      },
      {
        name: routeNames.FISH_MAP,
        path: 'map',
        component: mapComponent,
        beforeEnter: [newUserNavGuard],
        children: [
          ...queryRoutes,
          ...offlineRoutes,
          ...accountRoutes,
          ...myContentRoutes,
          ...routeBuilderRoutes,
          ...waterbodyRoutes,
          ...weatherRoutes,
          ...waterAccessRoutes,
          ...fishingAreaRoutes,
          ...regionsRoutes,
          ...searchRoutes,
        ],
      },
      ...fishRedeemRoutes,
      ...fishOnboardingRoutes,
    ],
  },
]
